"use client";

import {
    createContext,
    useCallback,
    useContext,
    useState,
    type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { getUserEmail } from "@/app/api/service/user";
import Avatar from "@/app/assets/icons/avatar";
import SideDrawer from "@/app/components/profile-drawer";
import { useCleverTapUser } from "@/app/hooks/use-clevertap";
import { shouldShowPushPromptNow } from "@/app/utils/push-notification-prompt";
import { useAuth } from "@/context/authcontext";
import type { User } from "@/app/interface/user";

type NavProfileContextValue = {
    isAuthenticated: boolean;
    isAuthFetched: boolean;
    openDrawer: () => void;
};

const NavProfileContext = createContext<NavProfileContextValue | null>(null);

type NavProfileProviderProps = {
    children: ReactNode;
};

/**
 * Shares post-auth avatar/drawer state across desktop and mobile header slots
 * without a second getUserEmail fetch (same React Query key as portfolio/unibo).
 */
export function NavProfileProvider({ children }: NavProfileProviderProps) {
    const { isAuthenticated, isAuthFetched } = useAuth();
    const { onUserLogin, isInitialized } = useCleverTapUser();
    const [isOpen, setIsOpen] = useState(false);

    const { data: user } = useQuery<User>({
        queryKey: ["userData"],
        queryFn: getUserEmail,
        enabled: isAuthenticated,
    });

    const openDrawer = useCallback(() => {
        setIsOpen(true);

        if (!user?.userId || !isInitialized || !shouldShowPushPromptNow()) {
            return;
        }

        onUserLogin(
            {
                identity: user.userId,
                name: user.name,
                email: user.email,
                phone: user.phone,
            },
            true,
        );
    }, [isInitialized, onUserLogin, user]);

    return (
        <NavProfileContext.Provider
            value={{ isAuthenticated, isAuthFetched, openDrawer }}
        >
            {children}
            {isAuthenticated && (
                <SideDrawer
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    position="right"
                    name={user?.name}
                    email={user?.email}
                    phone={user?.phone}
                    userId={user?.userId}
                />
            )}
        </NavProfileContext.Provider>
    );
}

export function useNavProfile(): NavProfileContextValue {
    const context = useContext(NavProfileContext);
    if (!context) {
        throw new Error("useNavProfile must be used within a NavProfileProvider");
    }
    return context;
}

/** Avatar entry point — visible only after authentication. */
export function NavAvatarButton() {
    const { isAuthenticated, openDrawer } = useNavProfile();

    if (!isAuthenticated) {
        return null;
    }

    return (
        <button
            type="button"
            onClick={openDrawer}
            aria-label="Open profile"
            className="cursor-pointer"
        >
            <Avatar />
        </button>
    );
}
